import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Lanyard from '../blocks/Lanyard-Old/Lanyard';
import VariableProximity from '../blocks/VariableProximity/VariableProximity';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import { getTranslations, Language } from "../utils/localization";
import './AboutMe.css';

const AboutMe: React.FC = () => {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const [language, setLanguage] = React.useState<Language>('en');
  const [translations, setTranslations] = React.useState(getTranslations('en'));

  // Update translations when language changes
  React.useEffect(() => {
    const handleLanguageChange = () => {
      const html = document.documentElement;
      const lang = html.lang === 'zh-CN' ? 'zh' : 'en';
      setLanguage(lang);
      setTranslations(getTranslations(lang));
    };

    // Initial check
    handleLanguageChange();

    // Listen for language changes
    window.addEventListener('languagechange', handleLanguageChange);
    return () => {
      window.removeEventListener('languagechange', handleLanguageChange);
    };
  }, []);

  // Language toggle function
  const toggleLanguage = (lang: Language) => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
    setLanguage(lang);
    setTranslations(getTranslations(lang));
    window.dispatchEvent(new Event('languagechange'));
  };

  return (
    <div className="about-me-page">
      <header className="header" ref={headerRef}>
        <div className="logo" onClick={() => navigate('/')}>
          <img src="/symbol_big.png" alt="Logo" className="logo-image" />
          <div className="logo-text">
            <VariableProximity
              containerRef={headerRef}
              label="Mengxian"
              fromFontVariationSettings="'wght' 600"
              toFontVariationSettings="'wght' 900, 'ital' 1"
              radius={100}
              falloff="gaussian"
              style={{ fontSize: '1.3rem', fontWeight: 600, lineHeight: '1' }}
            />
            <VariableProximity
              containerRef={headerRef}
              label="Gao"
              fromFontVariationSettings="'wght' 600"
              toFontVariationSettings="'wght' 900, 'ital' 1"
              radius={100}
              falloff="gaussian"
              style={{ fontSize: '1.3rem', fontWeight: 600, lineHeight: '1' }}
            />
          </div>
        </div>
        <nav className="nav">
          <div className="language-switcher">
            <button 
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => toggleLanguage('en')}
            >
              EN
            </button>
            <button 
              className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
              onClick={() => toggleLanguage('zh')}
            >
              中文
            </button>
          </div>
          <ul>
            <li><a href="/#about">{translations.nav.about}</a></li>
            <li><a href="/#projects">{translations.nav.projects}</a></li>
            <li><a href="/#contact">{translations.nav.contact}</a></li>
          </ul>
        </nav>
      </header>

      <main className="about-me-content">
        <div className="lanyard-wrapper">
          <Lanyard position={[0, 0, 25]} gravity={[0, -15, 0]} transparent={true} />
        </div>
        
        <div className="profile-container">
          <SpotlightCard className="profile-card" spotlightColor="rgba(0, 229, 255, 0.2)">
            <div className="profile-info">
              <h1>{translations.aboutMe.title}</h1>
              <div className="bio-text">
                {translations.aboutMe.bio.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
        
                <div className="skills-section">
                  <h2>{translations.aboutMe.skills.title}</h2>
                  <div className="skills-grid">
                    <div className="skill-category">
                      <h3>{translations.aboutMe.skills.categories.design.title}</h3>
                      <ul>
                        {translations.aboutMe.skills.categories.design.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="skill-category">
                      <h3>{translations.aboutMe.skills.categories.development.title}</h3>
                      <ul>
                        {translations.aboutMe.skills.categories.development.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="skill-category">
                      <h3>{translations.aboutMe.skills.categories.tools.title}</h3>
                      <ul>
                        {translations.aboutMe.skills.categories.tools.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SpotlightCard>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-left">
          <p><a href="mailto:mg6839@nyu.edu">mg6839@nyu.edu</a></p>
          <p>682-553-4790</p>
        </div>
        <div className="footer-right">
          <p>{translations.footer.copyright}</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutMe; 